import { useState, useEffect, useCallback } from 'react';
import { api } from '../lib/api';
import { OrgInventoryItem, AddEquipmentEntry, QuoteType, ResidentialItemType } from '../types/admin';

export interface OrgInventoryItemInput {
  name: string;
  type: 'Casegoods' | 'Modular Component' | 'Modular Equipment';
  kz: number;
  description?: string;
  notes?: string;
  imageBase64?: string;
  /** Send null to clear. */
  additionalManHours?: number | null;
  addEquipment?: AddEquipmentEntry[];
  quoteType?: QuoteType;
  // Accessorial service fields (commercial only)
  isAccessorial?: boolean;
  accessorialLabel?: string;
  accessorialOriginManHours?: number;
  accessorialDestinationManHours?: number;
  // Residential fields
  photo?: string;
  moveUnits?: number;
  cubicFeet?: number;
  weightInPounds?: number;
  bulky?: boolean;
  requiredDisassembly?: boolean;
  /** Send null to clear the category. */
  residentialType?: ResidentialItemType | null;
  roomId?: string;
  /** Dollars. Send null to clear. Packing items and equipment only. */
  unitCost?: number | null;
  /** Whole days. Send null to clear (item is purchased/consumed). */
  rentalDays?: number | null;
}

// ── Shared request de-duplication ──────────────────────────────────────────
//
// Several components can mount at once and each ask for the same org's
// inventory (e.g. the quote shell plus a sub-page). Share one in-flight
// request per org so the list is downloaded once, not N times in parallel.
const inflightLists = new Map<string, Promise<OrgInventoryItem[]>>();

function loadOrgInventory(orgId: string): Promise<OrgInventoryItem[]> {
  const existing = inflightLists.get(orgId);
  if (existing) return existing;
  const p = api
    .get<{ success: boolean; data: OrgInventoryItem[] }>(`/org-admin/${orgId}/inventory`)
    .then(res => res.data)
    .finally(() => inflightLists.delete(orgId));
  inflightLists.set(orgId, p);
  return p;
}

// ── Lazy item images ───────────────────────────────────────────────────────
//
// The list endpoint omits `imageBase64` (it can be ~1 MB per item) and sets
// `hasImage` instead. Images are fetched per item on demand and cached for
// the lifetime of the page.
const imageCache = new Map<string, Promise<string | null>>();

/** Fetch an item's image data URL (null when it has none). Cached per org/item. */
export function fetchOrgInventoryImage(orgId: string, itemId: string): Promise<string | null> {
  const key = `${orgId}/${itemId}`;
  const existing = imageCache.get(key);
  if (existing) return existing;
  const p = api
    .get<{ success: boolean; data: { imageBase64: string | null } }>(
      `/org-admin/${orgId}/inventory/${itemId}/image`,
    )
    .then(res => res.data.imageBase64 ?? null);
  // Don't cache failures — let the next render retry.
  p.catch(() => imageCache.delete(key));
  imageCache.set(key, p);
  return p;
}

/** Seed/replace the cached image for an item (after create/update). */
export function primeOrgInventoryImage(orgId: string, itemId: string, image: string | null): void {
  imageCache.set(`${orgId}/${itemId}`, Promise.resolve(image));
}

export function useOrgInventory(orgId: string) {
  const [items, setItems] = useState<OrgInventoryItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchItems = useCallback(async () => {
    if (!orgId) {
      setItems([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      setItems(await loadOrgInventory(orgId));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load inventory');
    } finally {
      setLoading(false);
    }
  }, [orgId]);

  useEffect(() => {
    fetchItems();
  }, [fetchItems]);

  const createItem = async (input: OrgInventoryItemInput): Promise<OrgInventoryItem> => {
    const res = await api.post<{ success: boolean; data: OrgInventoryItem }>(
      `/org-admin/${orgId}/inventory`,
      input,
    );
    primeOrgInventoryImage(orgId, res.data.id, res.data.imageBase64 ?? null);
    setItems(prev => [...prev, res.data].sort((a, b) => a.name.localeCompare(b.name)));
    return res.data;
  };

  const updateItem = async (
    id: string,
    input: Partial<OrgInventoryItemInput>,
  ): Promise<OrgInventoryItem> => {
    const res = await api.patch<{ success: boolean; data: OrgInventoryItem }>(
      `/org-admin/${orgId}/inventory/${id}`,
      input,
    );
    primeOrgInventoryImage(orgId, id, res.data.imageBase64 ?? null);
    setItems(prev => prev.map(item => (item.id === id ? res.data : item)));
    return res.data;
  };

  const deleteItem = async (id: string): Promise<void> => {
    await api.del(`/org-admin/${orgId}/inventory/${id}`);
    setItems(prev => prev.filter(item => item.id !== id));
  };

  const syncFromMaster = async (): Promise<{ added: number; updated: number }> => {
    const res = await api.post<{ success: boolean; data: { added: number; updated: number } }>(
      `/org-admin/${orgId}/inventory/sync-from-master`,
    );
    await fetchItems();
    return res.data;
  };

  return { items, loading, error, createItem, updateItem, deleteItem, syncFromMaster, refetch: fetchItems };
}
